import React, { useEffect, useState } from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaEnvelope,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhone,
  FaSearch,
  FaSpinner,
  FaWhatsapp,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/admin/AdminHeader';
import { opportunityService } from '../../services/opportunityService';
import { Opportunity, OpportunityContactFilter, OpportunityPage } from '../../types';
import { formatCNPJ, formatCurrency, formatDate, formatFuelType, formatPhone } from '../../utils/formatters';

const PAGE_SIZE = 50;
// Postos consultados na Receita por execução da busca em lote (uma consulta por segundo)
const CONTACT_BATCH_SIZE = 100;

const CONTACT_FILTERS: { value: OpportunityContactFilter; label: string }[] = [
  { value: 'COM_TELEFONE', label: 'Com telefone' },
  { value: 'COM_EMAIL', label: 'Com e-mail' },
  { value: 'COM_CONTATO', label: 'Com telefone ou e-mail' },
  { value: 'SEM_CONTATO', label: 'Sem contato' },
];

// Espera o usuário parar de digitar antes de buscar
const useDebounced = (value: string, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const formatCep = (cep: string) => {
  const digits = cep.replace(/\D/g, '');
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : cep;
};

const fullAddress = (item: Opportunity) => {
  const street = [item.logradouro, item.numero].filter(Boolean).join(', ');
  const line = [street, item.complemento, item.bairro].filter(Boolean).join(' - ');
  const city = [item.cidade && `${item.cidade}/${item.estado}`, item.cep && `CEP ${formatCep(item.cep)}`]
    .filter(Boolean)
    .join(' · ');
  return [line || item.endereco, city].filter(Boolean).join(' · ');
};

// Celular (DDD + 9 dígitos começando com 9) pode receber WhatsApp
const whatsappNumber = (phone: string) => {
  const digits = phone.replace(/\D/g, '').replace(/^55(?=\d{11}$)/, '');
  return digits.length === 11 && digits[2] === '9' ? `55${digits}` : null;
};

interface ContactRowProps {
  icon: React.ElementType;
  label: string;
  children?: React.ReactNode;
}

// Linha do bloco de contato; sem valor mostra "Não cadastrado"
const ContactRow: React.FC<ContactRowProps> = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon className={`mt-1 shrink-0 text-xs ${children ? 'text-[#84cc34]' : 'text-white/30'}`} />
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-white/40">{label}</p>
      {children ? (
        <div className="text-white/85 break-words">{children}</div>
      ) : (
        <p className="text-white/40 italic">Não cadastrado</p>
      )}
    </div>
  </div>
);

const inputClass =
  'px-3 py-2 bg-[#0f1419] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#089453]';

const AdminOpportunities: React.FC = () => {
  const [estado, setEstado] = useState('');
  const [cidade, setCidade] = useState('');
  const [busca, setBusca] = useState('');
  const [apenasComPreco, setApenasComPreco] = useState(false);
  const [contato, setContato] = useState<OpportunityContactFilter | ''>('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<OpportunityPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [lookingUp, setLookingUp] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);

  const cidadeBusca = useDebounced(cidade);
  const textoBusca = useDebounced(busca);

  // Filtro novo volta para a primeira página
  useEffect(() => {
    setPage(0);
  }, [estado, cidadeBusca, textoBusca, apenasComPreco, contato]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    opportunityService
      .list({
        estado,
        cidade: cidadeBusca,
        busca: textoBusca,
        apenasComPreco,
        contato: contato || undefined,
        page,
        size: PAGE_SIZE,
      })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((error: any) => {
        if (!cancelled) toast.error(error.response?.data?.message || 'Erro ao carregar oportunidades');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [estado, cidadeBusca, textoBusca, apenasComPreco, contato, page, reloadKey]);

  // Telefone/e-mail do posto pelo CNPJ na Receita; atualiza só o card
  const lookupContact = async (item: Opportunity) => {
    setLookingUp(item.id);
    try {
      const { result, message } = await opportunityService.lookupContact(item.id);
      setData((current) =>
        current && {
          ...current,
          items: current.items.map((i) =>
            i.id === item.id
              ? { ...i, telefone: result.telefone, email: result.email, contatoConsultadoEm: result.contatoConsultadoEm }
              : i
          ),
        }
      );
      if (result.atualizado) toast.success(message || 'Contato atualizado com os dados da Receita');
      else toast.info(message || 'A Receita não tem contato novo para este posto');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao consultar a Receita');
    } finally {
      setLookingUp(null);
    }
  };

  // Busca em lote nas oportunidades sem telefone ou e-mail ainda não consultadas (respeita UF e cidade)
  const lookupContacts = async () => {
    const scope = [estado && `UF ${estado}`, cidadeBusca && `cidade "${cidadeBusca}"`].filter(Boolean).join(', ');
    const ok = window.confirm(
      `Consultar na Receita até ${CONTACT_BATCH_SIZE} postos sem telefone ou e-mail` +
        `${scope ? ` (${scope})` : ''}? Uma consulta por segundo: pode levar uns ${Math.ceil(
          CONTACT_BATCH_SIZE / 60
        )} minutos.`
    );
    if (!ok) return;
    setBatchRunning(true);
    try {
      const run = await opportunityService.lookupContacts({ estado, cidade: cidadeBusca, limite: CONTACT_BATCH_SIZE });
      if (run.status === 'ERRO') toast.error(run.mensagem || 'Busca de contatos falhou');
      else toast.success(run.mensagem || 'Busca de contatos concluída');
      setReloadKey((k) => k + 1);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro na busca de contatos');
    } finally {
      setBatchRunning(false);
    }
  };

  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#0f1419] pb-24 lg:pb-0">
      <AdminHeader />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Oportunidades</h1>
        <p className="text-white/60 text-sm mt-1 mb-6">
          Postos do cadastro da ANP que ainda não estão no Abastece+. Eles não aparecem no app até o dono assumir o
          posto pelo CNPJ. Os preços, quando houver, são da pesquisa semanal da ANP.
        </p>

        <section className="flex flex-wrap items-center gap-3 mb-6">
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            className={inputClass}
            aria-label="UF"
          >
            <option value="">Todas as UFs</option>
            {(data?.estados ?? []).map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
          <input
            value={cidade}
            onChange={(e) => setCidade(e.target.value)}
            placeholder="Cidade"
            className={inputClass}
            aria-label="Cidade"
          />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome do posto ou CNPJ"
            className={`${inputClass} flex-1 min-w-[200px]`}
            aria-label="Nome do posto ou CNPJ"
          />
          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <input
              type="checkbox"
              checked={apenasComPreco}
              onChange={(e) => setApenasComPreco(e.target.checked)}
              className="accent-[#089453]"
            />
            Só com preço da ANP
          </label>
          <select
            value={contato}
            onChange={(e) => setContato(e.target.value as OpportunityContactFilter | '')}
            className={inputClass}
            aria-label="Contato"
          >
            <option value="">Qualquer contato</option>
            {CONTACT_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
          <button
            onClick={lookupContacts}
            disabled={batchRunning}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#089453]/60 text-[#84cc34] text-sm hover:bg-[#089453]/10 disabled:opacity-50"
            title="Busca telefone e e-mail pelo CNPJ na Receita dos postos ainda não consultados, respeitando UF e cidade"
          >
            {batchRunning ? <FaSpinner className="animate-spin" /> : <FaSearch />}
            {batchRunning ? 'Buscando contatos na Receita...' : 'Buscar contatos na Receita'}
          </button>
        </section>

        {isLoading && !data ? (
          <div className="flex justify-center py-20 text-[#089453]">
            <FaSpinner className="animate-spin" size={32} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-[#1a2332] border border-white/10 text-white/60">
            Nenhum posto encontrado com esses filtros.
          </div>
        ) : (
          <>
            <p className="text-xs text-white/50 mb-3">
              {from}–{to} de {total} {total === 1 ? 'posto' : 'postos'}
              {isLoading && ' · atualizando...'}
            </p>

            <div className="grid gap-3">
              {data.items.map((item) => (
                <article key={item.id} className="p-5 rounded-xl bg-[#1a2332] border border-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-1 min-w-0 flex-1">
                      <p className="text-white font-semibold">
                        {item.nomePosto}
                        {item.bandeira && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 align-middle">
                            {item.bandeira}
                          </span>
                        )}
                        {item.solicitacaoPendente && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 align-middle">
                            <FaClipboardCheck /> Solicitação pendente
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-white/60">
                        {[item.cnpj && `CNPJ ${formatCNPJ(item.cnpj)}`, item.cidade && `${item.cidade}/${item.estado}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>

                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm pt-3">
                        <ContactRow icon={FaPhone} label="Telefone">
                          {item.telefone && (
                            <span className="flex flex-wrap items-center gap-x-3">
                              <a href={`tel:${item.telefone}`} className="hover:text-white">
                                {formatPhone(item.telefone)}
                              </a>
                              {whatsappNumber(item.telefone) && (
                                <a
                                  href={`https://wa.me/${whatsappNumber(item.telefone)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-[#84cc34] hover:text-white"
                                >
                                  <FaWhatsapp /> WhatsApp
                                </a>
                              )}
                            </span>
                          )}
                        </ContactRow>
                        <ContactRow icon={FaEnvelope} label="E-mail">
                          {item.email && (
                            <a href={`mailto:${item.email}`} className="hover:text-white">
                              {item.email}
                            </a>
                          )}
                        </ContactRow>
                        <ContactRow icon={FaMapMarkerAlt} label="Endereço">
                          {fullAddress(item) && (
                            <>
                              {fullAddress(item)}
                              {item.latitude != null && item.longitude != null && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2 text-[#84cc34] hover:text-white whitespace-nowrap"
                                >
                                  Ver no mapa
                                </a>
                              )}
                            </>
                          )}
                        </ContactRow>
                        <ContactRow icon={FaIdCard} label="Cadastro ANP">
                          {(item.anpAutorizacao || item.anpCodigoIsimp) &&
                            [
                              item.anpAutorizacao && `Autorização ${item.anpAutorizacao}`,
                              item.anpCodigoIsimp && `Código ISIMP ${item.anpCodigoIsimp}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                        </ContactRow>
                      </div>

                      {item.cnpj && (!item.telefone || !item.email) && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-xs">
                          <button
                            onClick={() => lookupContact(item)}
                            disabled={lookingUp === item.id || batchRunning}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/15 text-white/85 hover:bg-white/5 disabled:opacity-50"
                          >
                            {lookingUp === item.id ? <FaSpinner className="animate-spin" /> : <FaSearch />}
                            Buscar contato na Receita
                          </button>
                          {item.contatoConsultadoEm && (
                            <span className="text-white/40">
                              Consultado na Receita em {formatDate(item.contatoConsultadoEm)}
                            </span>
                          )}
                        </div>
                      )}
                      {item.contatoConsultadoEm && item.telefone && item.email && (
                        <p className="pt-2 text-xs text-white/40">
                          Contato da Receita Federal (pode ser do responsável fiscal ou do contador)
                        </p>
                      )}
                    </div>

                    <div className="text-sm min-w-[220px]">
                      {item.precos.length === 0 ? (
                        <p className="text-white/40 italic">Sem preço recente da ANP</p>
                      ) : (
                        <table className="w-full">
                          <tbody>
                            {item.precos.map((price) => (
                              <tr key={price.tipoCombustivel}>
                                <td className="pr-3 py-0.5 text-white/70">{formatFuelType(price.tipoCombustivel)}</td>
                                <td className="py-0.5 text-right font-semibold text-white">
                                  {formatCurrency(price.preco)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {item.precos[0]?.coletadoEm && (
                        <p className="text-xs text-white/40 mt-1 text-right">
                          Pesquisa ANP de {formatDate(item.precos[0].coletadoEm)}
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {lastPage > 0 && (
              <div className="flex items-center justify-center gap-3 mt-6">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || isLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/5 disabled:opacity-40"
                >
                  <FaChevronLeft /> Anterior
                </button>
                <span className="text-sm text-white/60">
                  Página {page + 1} de {lastPage + 1}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={page >= lastPage || isLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/5 disabled:opacity-40"
                >
                  Próxima <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminOpportunities;
